import { Container, ProductTile, ScrollCue, Section } from '@vcn/ui'
import { useContent, type Party } from "../content";

function PartyBlock({ party, className }: { party: Party; className?: string }) {
  return (
    // Mobil linksbündig, ab sm zentriert – wie im Entwurf.
    <div className={`text-left sm:text-center ${className ?? ''}`}>
      <h3 className="text-xl font-bold text-white sm:text-2xl">{party.name}</h3>

      <ul className="mt-3 inline-flex flex-col gap-2 text-left text-[0.95rem] leading-snug text-white/90">
        {party.points.map((point) => (
          <li key={point.text} className="flex gap-2">
            <span aria-hidden="true" className="text-[var(--brand-accent)]">
              +
            </span>
            <span>
              {point.text}
              {point.note && (
                <>
                  <br />
                  <em className="text-white/80">({point.note})</em>
                </>
              )}
              {point.suffix && (
                <>
                  <br />
                  {point.suffix}
                </>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Senkrechte Linie mit Punkt am oberen Ende (Element 518/526). */
function StemDown({ className }: { className?: string }) {
  return (
    <div aria-hidden="true" className={`flex flex-col items-center ${className ?? ''}`}>
      <span className="size-1.5 rounded-full bg-[var(--brand-line)]" />
      <span className="h-16 w-px bg-[var(--brand-line)] sm:h-24" />
    </div>
  )
}

/**
 * Gabelung unter der Kachel. Boegen 1:1 aus „Element 520/521.svg“: je 260,03
 * breit und 155,72 hoch, unten senkrecht auslaufend, Strichstaerke 2,
 * Punkte r 4,51.
 *
 * Die beiden Elemente stehen im Entwurf NICHT auf Stoss: ihre Punkte liegen
 * dort 113,6 Einheiten auseinander (nachgemessen 904,5 und 1018,1 auf dem
 * 1920er Artboard) und damit deutlich sichtbar getrennt unter der Kachel.
 * Daraus ergibt sich auch die Breite: die Enden liegen bei 25,4 % und 74,6 %
 * der Inhaltsbreite, der Faecher nimmt also die halbe Spalte ein.
 */
function Fork() {
  return (
    <svg
      viewBox="-5 -5 643.66 165.72"
      aria-hidden="true"
      className="mx-auto h-auto w-1/2"
      fill="none"
    >
      <g stroke="#ccffce" strokeWidth="2" strokeMiterlimit="10">
        <path d="M260.03,0C250.92,75.75,0,77.17,0,155.72" />
        <path d="M373.63,0C382.74,75.75,633.66,77.17,633.66,155.72" />
      </g>
      <g fill="#ccffce">
        <circle cx="260.03" cy="0" r="4.51" />
        <circle cx="373.63" cy="0" r="4.51" />
      </g>
    </svg>
  );
}

/*
 * Gabelung unter der Kachel, mobile Fassung. Geometrie 1:1 aus
 * „Element 557/558.svg“, bezogen auf die Inhaltsbreite des mobilen Entwurfs
 * (375 − 2 × 23,5 Rand = 328): die beiden Punkte sitzen 16 Einheiten
 * auseinander unter der Kachel, von dort läuft je ein Bogen 73,62 nach außen
 * und 72,54 nach unten und endet senkrecht – links bei 82,38, rechts bei
 * 245,62.
 *
 * Im Entwurf ist der rechte Bogen samt der langen Senkrechten EIN Pfad. Hier
 * steht die Senkrechte als eigenes Element darunter, weil ihre Länge vom
 * Textblock abhängt. Damit sie nahtlos ansetzt, endet der viewBox genau am
 * Bogenende und beide teilen sich FORK_MOBILE_STEM_X.
 */
const FORK_MOBILE_STEM_X = `${((245.62 + 3) / 334) * 100}%`

function ForkMobile() {
  return (
    <svg
      viewBox="-3 -3 334 76"
      aria-hidden="true"
      className="mx-auto h-auto w-full text-[var(--brand-line)]"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    >
      <path d="M156,0C156,46.27,82.38,26.27,82.38,72.54" />
      <path d="M172,0C172,46.27,245.62,26.27,245.62,72.54" />
      <circle cx="156" cy="0" r="2.26" fill="currentColor" stroke="none" />
      <circle cx="172" cy="0" r="2.26" fill="currentColor" stroke="none" />
    </svg>
  )
}

export function Roles() {
  const { parties, ui } = useContent();

  return (
    <Section id="beteiligte" className="scroll-mt-20 pt-4">
      <Container>
        <PartyBlock party={parties.top} />

        <StemDown className="mt-8" />

        <ProductTile
          brand="carrierhub"
          width="clamp(7rem, 16vw, 13rem)"
          shadow={false}
          decorative
          className="mt-4"
        />

        {/* Ab sm stehen beide Partner nebeneinander … */}
        <div className="mt-4 hidden sm:block">
          <Fork />
        </div>
        <div className="mt-6 hidden gap-8 sm:grid sm:grid-cols-2">
          <PartyBlock party={parties.left} />
          <PartyBlock party={parties.right} />
        </div>

        {/* … mobil untereinander, mit durchlaufender Linie zum Netz-Partner. */}
        <div className="mt-4 sm:hidden">
          <ForkMobile />
        </div>
        <div className="sm:hidden">
          {/*
            Die Senkrechte setzt oben ohne Absatz am rechten Bogen an und endet
            knapp über „Netz-Partner“ – wie im Entwurf, wo beides ein Pfad ist.
            Deshalb umschließt der relative Kasten nur den GU-Partner-Block.
          */}
          <div className="relative pb-8">
            <span
              aria-hidden="true"
              className="absolute top-0 bottom-0 w-px -translate-x-1/2 bg-[var(--brand-line)]"
              style={{ left: FORK_MOBILE_STEM_X }}
            />
            <PartyBlock party={parties.left} className="pt-2 pr-28" />
          </div>

          {/* Im Entwurf beginnt der Netz-Partner bei 172 von 328 Einheiten,
              also knapp hinter der Mitte – dort trifft ihn die Senkrechte. */}
          <PartyBlock party={parties.right} className="mt-2 pl-[52%]" />
        </div>

        <ScrollCue
          href="#zielgruppen"
          label={ui.toAudiences}
          className="mt-16"
        />
      </Container>
    </Section>
  )
}
